import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Car,
  Building2,
  Users,
  CreditCard,
  LogOut,
  Menu,
  X,
  MessageSquare,
  CalendarCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';

const LG = 1024;

const DashboardLayout = ({ children }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= LG);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < LG);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < LG;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/app/login');
  };

  const getUserName = () => {
    if (user?.fullName) return user.fullName;
    if (user?.profile?.fullName) return user.profile.fullName;
    return user?.email || 'Usuario';
  };

  const getRoleLabel = () => {
    const roleLabels = {
      SUPER_ADMIN: 'Super Admin',
      AGENCY_ADMIN: 'Admin Agencia',
      SALES: 'Ventas',
      EDITOR: 'Editor',
      VIEWER: 'Visor'
    };
    return roleLabels[user?.role] || 'Usuario';
  };

  const navItems = isSuperAdmin
    ? [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/app/dashboard' },
        { icon: Building2, label: 'Agencias', path: '/app/admin/agencies' },
        { icon: Users, label: 'Usuarios', path: '/app/admin/users' },
        { icon: MessageSquare, label: 'Leads', path: '/app/leads' },
        { icon: CreditCard, label: 'Pagos', path: '/app/admin/billing' },
        { icon: CalendarCheck, label: 'Demos', path: '/app/admin/demos' }
      ]
    : [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/app/dashboard' },
        { icon: Car, label: 'Vehículos', path: '/app/cars' },
        { icon: MessageSquare, label: 'Leads', path: '/app/leads' },
        { icon: Building2, label: 'Mi Agencia', path: '/app/agency' }
      ];

  return (
    <div className="min-h-screen bg-slate-50">
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      <div className="flex h-screen overflow-hidden">
        <aside
          className={cn(
            "bg-slate-900 text-white w-64 flex-shrink-0 flex flex-col transition-all duration-300 z-50",
            isMobile
              ? cn("fixed inset-y-0 left-0", !sidebarOpen && "-translate-x-full")
              : cn(!sidebarOpen && "w-0 overflow-hidden")
          )}
          data-testid="sidebar"
        >
          <div className="p-5 border-b border-slate-800 flex items-center justify-between min-h-[64px]">
            <div className="overflow-hidden flex items-center gap-2.5">
              <img src="/logo.png" alt="AutoFlow" className="h-8 w-auto rounded-lg" />
              <div>
                <h1 className="text-lg font-bold whitespace-nowrap">AutoFlow</h1>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{getUserName()}</p>
              </div>
            </div>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-800 min-h-[44px] min-w-[44px] flex items-center justify-center ml-2"
                data-testid="sidebar-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
            {navItems.map((item) => {
              const active = location.pathname === item.path ||
                (item.path !== '/app/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors min-h-[44px]",
                    active
                      ? "bg-slate-800 text-white font-medium"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-3 border-t border-slate-800">
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 min-h-[44px]"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
              Cerrar Sesión
            </Button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-2 flex items-center justify-between flex-shrink-0 min-h-[56px]">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen((prev) => !prev)}
              className="min-h-[44px] min-w-[44px]"
              data-testid="toggle-sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900 truncate max-w-[160px]">{getUserName()}</p>
                <p className="text-xs text-slate-500">{getRoleLabel()}</p>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
